import { createBrowserRouter } from "react-router-dom";
import { lazy } from "react";
import RootLayout from "@/app/layouts/RootLayout";
import MainLayout from "@/app/layouts/MainLayout";
import AuthLayout from "@/app/layouts/AuthLayout";
import ProtectedRoute from "@/app/router/guards/ProtectedRoute";
import PublicRoute from "@/app/router/guards/PublicRoute";
import { dashboardRoutes } from "@/features/dashboard/routes";
import { tenantRoutes } from "@/features/tenants/routes";
import { apiKeyRoutes } from "@/features/api-keys/routes";
import { ROUTES } from "@/app/router/routes";
import { type NavItem, isRouteHandle, type RouteHandle } from "./router.types";

// Lazy load pages
const Login = lazy(() => import("@/features/auth/pages/Login"));
const Logout = lazy(() => import("@/features/auth/pages/Logout"));
const NotFound = lazy(() => import("@/shared/components/common/NotFound"));

export const router = createBrowserRouter([
  {
    path: "/",
    element: <RootLayout />,
    children: [
      // Protected routes (require authentication)
      {
        element: (
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        ),
        children: [
          ...dashboardRoutes,
          ...tenantRoutes,
          ...apiKeyRoutes,
          {
            path: ROUTES.logout.slice(1), // Remove leading /
            element: <Logout />,
            handle: { title: "Logout", protected: true },
          }
        ],
      },
      // Public routes (redirect to dashboard if authenticated)
      {
        path: "auth",
        element: (
          <PublicRoute>
            <AuthLayout />
          </PublicRoute>
        ),
        children: [
          {
            path: "login",
            element: (
              <PublicRoute>
                <Login />
              </PublicRoute>
            ),
            handle: { title: "Sign In", protected: false },
          }
        ],
      },
      // 404 catch-all route
      {
        path: "*",
        element: <NotFound />,
      },
    ],
  },
]);

// Generate navItems from router
const firstRoute = router.routes[0];
const protectedRoute = firstRoute?.children?.[0];
const protectedChildren = protectedRoute?.children ?? [];

export const navItems: NavItem[] = protectedChildren
  .filter(route => {
    const handle = route.handle as unknown;
    return isRouteHandle(handle) && handle.label !== undefined;
  })
  .map(route => {
    const handle = route.handle as RouteHandle;
    const routePath = route.path ?? '';

    return {
      href: route.path ? `/${routePath}` : '/',
      label: handle.label ?? '',
      icon: handle.icon,
      subNav: [],
    };
  });
